import { useMemo } from "react";
import useFetchGraphData from "../useFetchGraphData";
import {
  STATIONS,
  VARIABLES,
  WY_MONTHS,
  daysInWyMonth,
  DEFAULT_NORMAL_START,
} from "../../pages/climateMonthly/climateCopy";

/* Darker than the grey this used to be. That value was picked when the
   baseline was a solid bar, where grey has plenty of area to read against;
   as a 2px stroke the same grey nearly disappears behind saturated bars.
   #3F3F3F clears 10:1 on white, so it is legible and accessible without
   competing with the measurements, and it is a neutral so it stays clear of
   every hue in the bar palette for all forms of colour vision. */
const NORMAL_COLOR = "#3F3F3F";

/* Colorblind-safe pairs, one pair per water year on the chart. The selected
   water year takes the first pair, comparison years the rest. */
const YEAR_COLORS = [
  { 155: "#0072B2", 156: "#D55E00" },
  { 155: "#009E73", 156: "#CC79A7" },
  { 155: "#56B4E9", 156: "#E69F00" },
  { 155: "#332288", 156: "#882255" },
];

export const NORMAL_KEY = "normal";
export const seriesKeyFor = (locationid, waterYear) =>
  `s${locationid}_${waterYear}`;

const fToC = (f) => ((f - 32) * 5) / 9;

/**
 * Pure transform behind the hook: turns the two API payloads into the twelve
 * water-year month rows the chart and table read. Exported so it can be
 * exercised without React.
 */
export function buildClimateSeries({
  monthly,
  normals,
  years,
  locationids,
  config,
  units,
  normalStart,
}) {
  const empty = {
    series: [],
    seriesKeys: [],
    cellMeta: {},
    incompleteNotes: [],
    totals: {},
    normalLabel: "",
    normalYears: null,
  };
  if (!monthly || !normals) return empty;

  const toDisplay = (value) => {
    if (value == null) return null;
    const number = Number(value);
    if (!Number.isFinite(number)) return null;
    return config.isTemperature && units === "c" ? fToC(number) : number;
  };

  /* ---- legend entries, normal first then one per station and year ---- */
  const normalEnd = normals.reduce(
    (max, row) => Math.max(max, Number(row.normal_end_wy) || 0),
    0
  );
  const normalYears = normals.length
    ? Math.min(...normals.map((row) => Number(row.years_in_normal)))
    : null;
  const normalLabel = normalEnd
    ? `${normalStart} to ${normalEnd} average (${STATIONS.KAPA.label})`
    : `${normalStart} average (${STATIONS.KAPA.label})`;

  const seriesKeys = [
    {
      key: NORMAL_KEY,
      label: normalLabel,
      color: NORMAL_COLOR,
      isNormal: true,
      locationid: STATIONS.KAPA.locationid,
      waterYear: null,
    },
  ];

  years.forEach((year, yearIndex) => {
    const palette = YEAR_COLORS[yearIndex % YEAR_COLORS.length];
    locationids.forEach((locationid) => {
      const station =
        locationid === STATIONS.MHFD.locationid ? STATIONS.MHFD : STATIONS.KAPA;
      seriesKeys.push({
        key: seriesKeyFor(locationid, year),
        label: `${station.label} WY${year}`,
        color: palette[locationid],
        isNormal: false,
        locationid,
        waterYear: year,
      });
    });
  });

  /* ---- twelve fixed slots, filled from whatever came back ---- */
  const series = WY_MONTHS.map((m) => ({
    month: m.abbrev,
    monthNum: m.monthNum,
    wy_month_order: m.order,
  }));
  const byOrder = new Map(series.map((row) => [row.wy_month_order, row]));

  const normalByOrder = new Map();
  normals.forEach((row) => {
    const order = Number(row.wy_month_order);
    const value = toDisplay(row[config.normalField]);
    normalByOrder.set(order, value);
    const target = byOrder.get(order);
    if (target) target[NORMAL_KEY] = value;
  });

  const cellMeta = {};
  const incompleteNotes = [];

  monthly.forEach((row) => {
    const locationid = Number(row.locationid);
    const year = Number(row.water_year);
    if (!locationids.includes(locationid) || !years.includes(year)) return;

    const order = Number(row.wy_month_order);
    const target = byOrder.get(order);
    if (!target) return;

    const key = seriesKeyFor(locationid, year);
    const value = toDisplay(row[config.field]);
    target[key] = value;

    const daysWithData = Number(row.days_with_data);
    const daysInMonth = daysInWyMonth(year, Number(row.month_num));
    const incomplete =
      Number.isFinite(daysWithData) && daysWithData < daysInMonth;
    const normalValue = normalByOrder.get(order);

    cellMeta[`${key}|${order}`] = {
      daysWithData,
      daysInMonth,
      incomplete,
      departure:
        value != null && normalValue != null ? value - normalValue : null,
    };

    if (incomplete) {
      const station =
        locationid === STATIONS.MHFD.locationid ? STATIONS.MHFD : STATIONS.KAPA;
      incompleteNotes.push({
        key,
        order,
        text: `${station.label} WY${year} ${row.month_abbrev}: ${daysWithData} of ${daysInMonth} days`,
      });
    }
  });

  /* Water year totals for the table's last row. Precipitation only, and
     only where the year is whole, so a part-year total is not read as a
     full one. */
  const totals = {};
  if (config.hasWaterYearTotal) {
    seriesKeys.forEach(({ key }) => {
      const values = series.map((row) => row[key]);
      const complete = values.every((value) => value != null);
      totals[key] = complete
        ? values.reduce((sum, value) => sum + value, 0)
        : null;
    });
  }

  incompleteNotes.sort((a, b) => a.order - b.order);

  return {
    series,
    seriesKeys,
    cellMeta,
    incompleteNotes,
    totals,
    normalLabel,
    normalYears,
  };
}

/**
 * Builds the grouped-bar series for the Monthly Climate Comparison chart.
 *
 * Returns twelve rows, October through September, whatever the data cover, so
 * a station with no reading in a month yields null and the chart leaves a gap
 * instead of drawing a zero.
 */
export default function useClimateMonthlySeries({
  variable = "precip",
  waterYear,
  compareYears = [],
  normalStart = DEFAULT_NORMAL_START,
  showKapa = true,
  showMhfd = true,
  units = "f",
}) {
  const config = VARIABLES[variable] ?? VARIABLES.precip;

  /* Newest year first so the selected water year leads the legend. */
  const years = useMemo(() => {
    const unique = [];
    [waterYear, ...compareYears].forEach((y) => {
      const value = Number(y);
      if (Number.isInteger(value) && !unique.includes(value))
        unique.push(value);
    });
    return unique;
  }, [waterYear, compareYears]);

  /* Either station can be switched off. The long-term average is fetched
     separately and still drawn, so turning both off leaves the baseline on
     its own, which is a legitimate thing to want to look at. */
  const locationids = useMemo(() => {
    const ids = [];
    if (showKapa) ids.push(STATIONS.KAPA.locationid);
    if (showMhfd) ids.push(STATIONS.MHFD.locationid);
    return ids;
  }, [showKapa, showMhfd]);

  const {
    data: monthly,
    isLoading: monthlyLoading,
    isFetching: monthlyFetching,
    isError: monthlyError,
  } = useFetchGraphData(
    {
      water_years: years.join(","),
      locationids: locationids.join(","),
    },
    "climate-monthly"
  );

  const {
    data: normals,
    isLoading: normalsLoading,
    isFetching: normalsFetching,
    isError: normalsError,
  } = useFetchGraphData(
    {
      locationid: STATIONS.KAPA.locationid,
      normal_start: normalStart,
    },
    "climate-monthly/normals"
  );

  const result = useMemo(
    () =>
      buildClimateSeries({
        monthly,
        normals,
        years,
        locationids,
        config,
        units,
        normalStart,
      }),
    [monthly, normals, years, locationids, config, units, normalStart]
  );

  return {
    ...result,
    variableConfig: config,
    unit: config.isTemperature && units === "c" ? "°C" : config.unit,
    /* Spelt out, for prose and for the screen-reader summary: "in in"
       reads badly, and a screen reader does not say "°F" usefully. */
    unitLong: config.isTemperature
      ? units === "c"
        ? "degrees Celsius"
        : "degrees Fahrenheit"
      : "inches",
    axisLabel: config.isTemperature
      ? `${config.axisLabel} (${units === "c" ? "°C" : "°F"})`
      : config.axisLabel,
    /* react-query's isLoading, not isFetching: with keepPreviousData the
       previous chart stays on screen while a new water year loads, instead of
       the whole chart and its filter chips unmounting on every toggle. */
    isLoading: monthlyLoading || normalsLoading,
    isFetching: monthlyFetching || normalsFetching,
    isError: monthlyError || normalsError,
  };
}

/**
 * Every water year each station has data for, newest first, for the year
 * pickers. Returns the union across both stations: KAPA back to 1998 and
 * Cherry Creek MET from 2021.
 */
export function useClimateWaterYears() {
  const { data, isLoading, isError } = useFetchGraphData(
    {},
    "climate-monthly/water-years"
  );

  const waterYears = useMemo(() => {
    if (!data?.length) return [];
    const unique = new Set(data.map((row) => Number(row.water_year)));
    return [...unique].sort((a, b) => b - a);
  }, [data]);

  return { waterYears, isLoading, isError };
}
